import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { summarize, type LinkPreview, type Mention, type Msg } from "../db";
import { formatDuration, recordVoice, type Recording } from "../media";
import { Avatar } from "./Avatar";
import * as S from "../state";
import { firstURL } from "./Bubble";
import { editing, replyTo, sendingFiles, sharing } from "./chatui";
import { EmojiPicker } from "./Emoji";
import { wrapSelection } from "./format";
import { Icon } from "./Icon";
import { engine, toast } from "./Main";
import { attachRequest, slashCommands } from "./Shortcuts";
import { StickerPicker } from "./Stickers";
import { recordingNote, VideoNoteRecorder } from "./VideoNote";

/** The quote for a reply; replying privately to a group message carries the
 * group and what the message said (it isn't in this chat). */
function quoteOf(m: Msg, chat: string) {
  return m.chat === chat ? { id: m.id, from: m.from } : { id: m.id, from: m.from, chat: m.chat, kind: m.kind, text: summarize(m).slice(0, 200) };
}

export function Composer({ jid }: { jid: string }) {
  const e = engine.value;
  const [text, setText] = useState(() => e?.draft(jid) ?? "");
  const [emoji, setEmoji] = useState(false);
  const [attach, setAttach] = useState(false);
  const [stickerOpen, setStickerOpen] = useState(false);
  const [rec, setRec] = useState<Recording | null>(null);
  const ta = useRef<HTMLTextAreaElement>(null);
  const mediaInput = useRef<HTMLInputElement>(null);
  const docInput = useRef<HTMLInputElement>(null);
  const edit = editing.value;
  const reply = replyTo.value;
  const preview = useLinkPreview(text);
  // @mentions (groups): picked members and the in-progress "@query".
  const [picked, setPicked] = useState<Map<string, string>>(new Map());
  const [suggest, setSuggest] = useState<{ query: string; start: number } | null>(null);
  const group = S.isGroup(jid);
  const candidates = suggest
    ? (S.groups.value.get(jid)?.members ?? [])
        .map((m) => m.jid)
        .filter((j) => j !== e?.me)
        .filter((j) => {
          const q = suggest.query.toLowerCase();
          return !q || S.memberName(j).toLowerCase().replace(/^~/, "").includes(q) || j.startsWith(q);
        })
        .slice(0, 8)
    : [];
  // "@everyone" mentions the whole group (everyone gets notified).
  const everyone = !!suggest && group && "everyone".startsWith(suggest.query.toLowerCase());
  if (everyone) candidates.unshift(jid);

  // "/poll", "/call"…: typed at the start of an empty message.
  const slash = /^\/(\w*)$/.exec(text)?.[1];
  const commands = slash !== undefined && !edit ? slashCommands(jid).filter((c) => c.cmd.startsWith(slash.toLowerCase())) : [];
  const [slashAt, setSlashAt] = useState(0);
  useEffect(() => setSlashAt(0), [slash]);
  function runSlash(i: number) {
    const c = commands[i];
    if (!c) return;
    setBoxText("", 0);
    c.run();
  }

  // The command palette / a slash command asks for the photo or file picker.
  useEffect(() => {
    const want = attachRequest.value;
    if (!want) return;
    attachRequest.value = null;
    (want === "media" ? mediaInput : docInput).current?.click();
  }, [attachRequest.value]);

  function mentionsIn(body: string): Mention[] {
    const out: Mention[] = [];
    for (const [jid, label] of picked) {
      const at = body.indexOf("@" + label);
      if (at >= 0) out.push({ jid, begin: at, end: at + label.length + 1 });
    }
    return out;
  }

  function choose(j: string) {
    if (!suggest) return;
    const label = j === jid ? "everyone" : S.memberName(j).replace(/^~/, "");
    const caret = ta.current?.selectionStart ?? text.length;
    const next = text.slice(0, suggest.start) + "@" + label + " " + text.slice(caret);
    setPicked(new Map(picked).set(j, label));
    setSuggest(null);
    setBoxText(next, suggest.start + label.length + 2);
  }

  useEffect(() => {
    // Opening a chat focuses the box, unless you're typing somewhere else
    // already (the command palette, a dialog, search).
    const a = document.activeElement;
    if (!a || a === document.body || a.closest(".chat")) ta.current?.focus();
    return () => e?.saveDraft(jid, ta.current?.value ?? "");
  }, [jid]);

  // Entering edit mode puts the message text in the box.
  useEffect(() => {
    if (edit) {
      setText(edit.body);
      ta.current?.focus();
    }
  }, [edit?.key]);

  useEffect(() => {
    if (reply) ta.current?.focus();
  }, [reply?.key]);

  useLayoutEffect(() => {
    const el = ta.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  }, [text]);

  function send() {
    const body = text.trim();
    if (!e) return;
    const p = preview.value && body.includes(preview.value.url) ? preview.value : undefined;
    if (edit) {
      if (body || edit.kind !== "text") void e.edit(edit, body, p);
      editing.value = null;
    } else if (body) {
      void e.sendText(jid, body, { replyTo: reply ? quoteOf(reply, jid) : undefined, preview: p, mentions: mentionsIn(body) });
      setPicked(new Map());
      replyTo.value = null;
    } else return;
    setText("");
    preview.reset();
    e.saveDraft(jid, "");
  }

  /** Replace the text and move the caret *synchronously*, so keys typed
   * right after (before Preact re-renders) land in the right place. */
  function setBoxText(next: string, caret: number) {
    const el = ta.current;
    if (el) {
      el.value = next;
      el.focus();
      el.selectionStart = el.selectionEnd = caret;
    }
    setText(next);
  }

  function insert(s: string) {
    const el = ta.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    setBoxText(text.slice(0, start) + s + text.slice(end), start + s.length);
  }

  async function startRecording() {
    try {
      setRec(await recordVoice());
    } catch (err) {
      toast("Microphone not available: " + (err instanceof Error ? err.message : String(err)));
    }
  }

  async function stopRecording(sendIt: boolean) {
    const r = rec;
    setRec(null);
    if (!r) return;
    if (!sendIt) return r.cancel();
    const prepared = await r.stop();
    if (prepared && e) {
      await e.sendDraft(jid, { body: "", kind: prepared.kind, media: prepared.media, replyTo: reply ? quoteOf(reply, jid) : undefined });
      replyTo.value = null;
    }
  }

  function pickFiles(ev: Event) {
    const input = ev.currentTarget as HTMLInputElement;
    const files = [...(input.files ?? [])];
    input.value = "";
    if (files.length) sendingFiles.value = files;
  }

  if (rec) return <RecordingBar rec={rec} onCancel={() => void stopRecording(false)} onSend={() => void stopRecording(true)} />;

  const quoted = edit ?? reply;
  return (
    <footer class="composer-wrap">
      {recordingNote.value === jid && <VideoNoteRecorder jid={jid} />}
      {quoted && (
        <div class="compose-quote">
          <div class={"quote" + (quoted.from === e?.me ? " mine" : "")}>
            <span class="quote-author">
              {edit ? "Edit message" : quoted.from === e?.me ? "You" : S.displayName(quoted.from)}
              {!edit && quoted.chat !== jid && ` · ${S.displayName(quoted.chat)}`}
            </span>
            <span class="quote-text">{summarize(quoted)}</span>
          </div>
          <button
            class="icon-btn"
            aria-label="Cancel"
            onClick={() => {
              if (edit) setText("");
              editing.value = null;
              replyTo.value = null;
            }}
          >
            <Icon name="close" />
          </button>
        </div>
      )}
      {preview.value && (
        <div class="compose-preview">
          {preview.value.thumb && <img src={preview.value.thumb} alt="" />}
          <div>
            <strong>{preview.value.title}</strong>
            <small>{preview.value.description ?? preview.value.site_name}</small>
          </div>
          <button class="icon-btn" aria-label="Remove link preview" onClick={preview.dismiss}>
            <Icon name="close" />
          </button>
        </div>
      )}
      {commands.length > 0 && (
        <ul class="mention-list slash-list" role="listbox" aria-label="Commands">
          {commands.map((c, i) => (
            <li key={c.cmd} role="option" aria-selected={i === slashAt} class={i === slashAt ? "on" : ""} onMouseDown={(ev) => (ev.preventDefault(), runSlash(i))}>
              <span class="slash-icon">
                <Icon name={c.icon as "poll"} size={18} />
              </span>
              <span class="slash-cmd">/{c.cmd}</span>
              <span class="muted">{c.label}</span>
            </li>
          ))}
        </ul>
      )}
      {suggest && candidates.length > 0 && (
        <ul class="mention-list" role="listbox" aria-label="Mention a participant">
          {candidates.map((j, i) => (
            <li key={j} role="option" aria-selected={i === 0} class={i === 0 ? "on" : ""} onMouseDown={(ev) => (ev.preventDefault(), choose(j))}>
              {j === jid ? (
                <span class="mention-all" aria-hidden="true">
                  @
                </span>
              ) : (
                <Avatar url={S.profiles.value.get(j)?.avatar_url} jid={j} name={S.memberName(j)} size={32} />
              )}
              <span>{j === jid ? "everyone · notify all members" : S.memberName(j)}</span>
            </li>
          ))}
        </ul>
      )}
      <div class="composer">
        <div class="menu-wrap">
          <button class="icon-btn" aria-label="Emoji" onClick={() => setEmoji(!emoji)}>
            <Icon name="smiley" />
          </button>
          {emoji && <EmojiPicker class="compose-picker" onClose={() => setEmoji(false)} onPick={insert} />}
        </div>
        {!edit && !S.isChannel(jid) && (
          <div class="menu-wrap">
            <button class={"icon-btn" + (stickerOpen ? " on-plain" : "")} aria-label="Stickers" title="Stickers" onClick={() => setStickerOpen(!stickerOpen)}>
              <Icon name="sticker" />
            </button>
            {stickerOpen && <StickerPicker jid={jid} onClose={() => setStickerOpen(false)} />}
          </div>
        )}
        {!edit && (
          <div class="menu-wrap">
            <button class={"icon-btn" + (attach ? " on" : "")} aria-label="Attach" onClick={() => setAttach(!attach)}>
              <Icon name="plus" />
            </button>
            {attach && (
              <AttachMenu
                onClose={() => setAttach(false)}
                // Polls and events need answers back: not in channels or broadcast lists.
                extras={!S.isChannel(jid) && !S.isBroadcast(jid)}
                onPick={(k) => (k === "videonote" ? (recordingNote.value = jid) : k === "media" || k === "docs" ? (k === "media" ? mediaInput : docInput).current?.click() : (sharing.value = k))}
              />
            )}
          </div>
        )}
        {/* always mounted, so the file dialog outlives the menu */}
        <input ref={mediaInput} class="file-input" type="file" accept="image/*,video/*" multiple hidden onChange={pickFiles} />
        <input ref={docInput} class="file-input" type="file" multiple hidden onChange={pickFiles} />
        <textarea
          ref={ta}
          rows={1}
          value={text}
          placeholder="Message  ·  / for commands"
          aria-label="Type a message"
          onInput={(ev) => {
            setText(ev.currentTarget.value);
            if (group) {
              const caret = ev.currentTarget.selectionStart ?? ev.currentTarget.value.length;
              const m = /(?:^|\s)@([^\s@]{0,30})$/.exec(ev.currentTarget.value.slice(0, caret));
              setSuggest(m ? { query: m[1], start: caret - m[1].length - 1 } : null);
            }
            if (ev.currentTarget.value) e?.composing(jid);
            else e?.stopComposing(jid, true);
          }}
          onKeyDown={(ev) => {
            // Formatting, WhatsApp desktop's keys: wrap the selection in markers.
            const mod = /Mac|iPhone|iPad/.test(navigator.platform) ? ev.metaKey : ev.ctrlKey;
            const marker = mod && !ev.altKey ? (ev.shiftKey ? { KeyX: "~", KeyM: "```" } : { KeyB: "*", KeyI: "_" })[ev.code as "KeyX"] : undefined;
            if (marker) {
              ev.preventDefault();
              setText(wrapSelection(ev.currentTarget, marker));
              return;
            }
            if (commands.length && (ev.key === "ArrowDown" || ev.key === "ArrowUp")) {
              ev.preventDefault();
              setSlashAt((a) => (a + (ev.key === "ArrowDown" ? 1 : commands.length - 1)) % commands.length);
            } else if (commands.length && (ev.key === "Enter" || ev.key === "Tab")) {
              ev.preventDefault();
              runSlash(slashAt);
            } else if (suggest && candidates.length && (ev.key === "Enter" || ev.key === "Tab")) {
              ev.preventDefault();
              choose(candidates[0]);
            } else if (ev.key === "Enter" && !ev.shiftKey && !ev.isComposing) {
              ev.preventDefault();
              send();
            } else if (ev.key === "Escape" && suggest) {
              setSuggest(null);
            } else if (ev.key === "Escape") {
              if (edit) setText("");
              editing.value = null;
              replyTo.value = null;
            } else if (ev.key === "ArrowUp" && !text) {
              // Like WhatsApp desktop: up-arrow edits your last message.
              const last = [...S.messages.value].reverse().find((m) => m.dir === "out");
              if (last && e?.canEdit(last)) (ev.preventDefault(), (editing.value = last));
            }
          }}
          onPaste={(ev) => {
            const files = [...(ev.clipboardData?.files ?? [])];
            if (files.length) {
              ev.preventDefault();
              sendingFiles.value = files;
            }
          }}
        />
        {text.trim() || edit ? (
          <button
            class="icon-btn send"
            // Tapping Send mustn't take the focus off the text box (on a
            // phone that closes the keyboard after every message).
            onPointerDown={(ev) => ev.preventDefault()}
            onMouseDown={(ev) => ev.preventDefault()}
            onClick={send}
            aria-label={edit ? "Save edit" : "Send"}
          >
            <Icon name={edit ? "check" : "send"} />
          </button>
        ) : (
          <button class="icon-btn send" onClick={() => void startRecording()} aria-label="Record voice message">
            <Icon name="mic" />
          </button>
        )}
      </div>
    </footer>
  );
}

function AttachMenu({ onClose, onPick, extras }: { onClose: () => void; onPick: (kind: "media" | "docs" | "contact" | "location" | "poll" | "event" | "videonote") => void; extras: boolean }) {
  const ref = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const onDown = (ev: MouseEvent) => ref.current && !ref.current.contains(ev.target as Node) && onClose();
    setTimeout(() => document.addEventListener("mousedown", onDown));
    return () => document.removeEventListener("mousedown", onDown);
  }, []);
  return (
    <ul class="menu attach-menu" ref={ref} role="menu">
      <li role="menuitem" onClick={() => (onClose(), onPick("docs"))}>
        <span class="attach-icon attach-doc">
          <Icon name="document" size={20} />
        </span>
        Document
      </li>
      <li role="menuitem" onClick={() => (onClose(), onPick("media"))}>
        <span class="attach-icon attach-photo">
          <Icon name="image" size={20} />
        </span>
        Photos &amp; videos
      </li>
      <li role="menuitem" onClick={() => (onClose(), onPick("videonote"))}>
        <span class="attach-icon attach-photo">
          <Icon name="videocam" size={20} />
        </span>
        Video note
      </li>
      <li role="menuitem" onClick={() => (onClose(), onPick("contact"))}>
        <span class="attach-icon attach-contact">
          <Icon name="person" size={20} />
        </span>
        Contact
      </li>
      <li role="menuitem" onClick={() => (onClose(), onPick("location"))}>
        <span class="attach-icon attach-location">
          <Icon name="pin" size={20} />
        </span>
        Location
      </li>
      {extras && (
        <li role="menuitem" onClick={() => (onClose(), onPick("poll"))}>
          <span class="attach-icon attach-poll">
            <Icon name="poll" size={20} />
          </span>
          Poll
        </li>
      )}
      {extras && (
        <li role="menuitem" onClick={() => (onClose(), onPick("event"))}>
          <span class="attach-icon attach-event">
            <Icon name="event" size={20} />
          </span>
          Event
        </li>
      )}
    </ul>
  );
}

function RecordingBar({ rec, onCancel, onSend }: { rec: Recording; onCancel: () => void; onSend: () => void }) {
  const [, tick] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);
  useEffect(() => {
    let raf = 0;
    let last = 0;
    const loop = (t: number) => {
      if (t - last > 80) {
        last = t;
        setLevels((l) => [...l.slice(-59), rec.level()]);
        tick((n) => n + 1);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [rec]);
  return (
    <footer class="composer-wrap">
      <div class="composer recording">
        <button class="icon-btn" onClick={onCancel} aria-label="Cancel recording">
          <Icon name="trash" />
        </button>
        <span class="rec-dot" />
        <span class="rec-time">{formatDuration((Date.now() - rec.startedAt) / 1000)}</span>
        <div class="rec-levels" aria-hidden="true">
          {levels.map((l, i) => (
            <span key={i} style={{ height: `${Math.max(8, l * 100)}%` }} />
          ))}
        </div>
        <button class="icon-btn send rec-send" onClick={onSend} aria-label="Send voice message">
          <Icon name="send" />
        </button>
      </div>
    </footer>
  );
}

/** Fetches a preview for the first URL in the text (debounced, dismissable). */
function useLinkPreview(text: string) {
  const [value, setValue] = useState<LinkPreview | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const url = firstURL(text);
  useEffect(() => {
    if (!url || url === dismissed) {
      setValue(null);
      return;
    }
    if (value?.url === url) return;
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .linkPreview(url)
        .then((p) => !cancelled && setValue(p))
        .catch(() => !cancelled && setValue(null));
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [url, dismissed]);
  return {
    value: value && value.url === url ? value : null,
    dismiss: () => {
      setDismissed(url ?? null);
      setValue(null);
    },
    reset: () => {
      setDismissed(null);
      setValue(null);
    },
  };
}
